import { Calendar, Megaphone, Sparkles, Tag, Radio } from "lucide-react";
import { cn } from "@/lib/cn";
import { safeHref } from "@/lib/safeUrl";
import type { TickerEvent, TickerEventKind } from "@/types/events";

const KIND_ICON: Record<TickerEventKind, React.ComponentType<{ className?: string }>> = {
  live: Radio,
  menu: Sparkles,
  promo: Tag,
  announcement: Megaphone,
  info: Calendar,
};

const KIND_ACCENT: Record<TickerEventKind, string> = {
  live: "text-rose-400",
  menu: "text-amber-300",
  promo: "text-emerald-400",
  announcement: "text-sky-400",
  info: "text-zinc-300 light:text-zinc-700",
};

interface TickerItemProps {
  event: TickerEvent;
}

export function TickerItem({ event }: TickerItemProps) {
  const kind: TickerEventKind = event.kind ?? "info";
  const Icon = KIND_ICON[kind];

  const content = (
    <span className="inline-flex items-center gap-2 whitespace-nowrap px-4 text-sm font-medium tracking-wide text-zinc-100 light:text-zinc-900 sm:px-6">
      <Icon className={cn("h-4 w-4 shrink-0", KIND_ACCENT[kind])} aria-hidden />
      <span>{event.text}</span>
      <span aria-hidden className="ml-4 text-zinc-600 sm:ml-6">
        •
      </span>
    </span>
  );

  // Operator-authored URL — render as a link only when the scheme is safe.
  const href = safeHref(event.href);
  if (href) {
    return (
      <a
        href={href}
        className="transition-opacity hover:opacity-80 focus-visible:opacity-80 focus-visible:outline-none"
      >
        {content}
      </a>
    );
  }

  return content;
}
